(() => {
  const C = window.JoffzProductConfigurator;
  if (!C || C.prototype.__jpcV46SemanticCompatibility) return;
  C.prototype.__jpcV46SemanticCompatibility = true;

  const basePrepareIndexes = C.prototype.prepareIndexes;
  const baseFindVariant = C.prototype.findVariant;
  const baseFindClosestExistingSize = C.prototype.findClosestExistingSize;
  const baseStateFromVariant = C.prototype.stateFromVariant;

  const normalizeSemanticSource = (value) =>
    String(value || '')
      .trim()
      .toLocaleLowerCase('en')
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ');

  C.prototype.canonicalFinish = function canonicalFinishV46(value) {
    const normalized = normalizeSemanticSource(value);
    if (!normalized) return null;

    const hasCanvas = normalized.includes('canvas');
    const hasFloating = normalized.includes('floating');
    const hasPoster = normalized.includes('poster') || normalized.includes('print');
    const hasBlack = normalized.includes('black');
    const hasOak = normalized.includes('oak') || normalized.includes('natural wood');
    const hasWhite = normalized.includes('white');

    if (normalized === 'frameless canvas' || normalized === 'stretched canvas' || normalized === 'canvas') {
      return 'canvas:unframed';
    }

    if (hasCanvas || hasFloating) {
      if (hasBlack) return 'canvas:black';
      if (hasOak) return 'canvas:oak';
      if (hasWhite) return 'canvas:white';
      return 'canvas:unframed';
    }

    if (normalized === 'framed') return 'paper:black';

    if (hasPoster || normalized === 'poster') {
      if (hasBlack) return 'paper:black';
      if (hasOak) return 'paper:oak';
      if (hasWhite) return 'paper:white';
      return 'paper:print-only';
    }

    if (normalized === 'black frame' || normalized === 'black framed') return 'paper:black';
    if (normalized === 'oak frame' || normalized === 'oak framed' || normalized === 'natural wood frame') return 'paper:oak';
    if (normalized === 'white frame' || normalized === 'white framed') return 'paper:white';
    if (normalized === 'print only') return 'paper:print-only';

    return null;
  };

  C.prototype.canonicalVariantFinish = function canonicalVariantFinishV46(variant) {
    const finish = variant?.options?.[this.finishOptionKey];
    let semantic = this.canonicalFinish(finish);
    if (!semantic) return null;

    const auxiliaryValues = Object.entries(variant?.options || {})
      .filter(([key]) => key !== this.finishOptionKey && key !== this.sizeOptionKey)
      .map(([, value]) => normalizeSemanticSource(value));

    const explicitCanvasType = auxiliaryValues.some(
      (value) => value === 'canvas' || value === 'stretched canvas'
    );
    const explicitPaperType = auxiliaryValues.some(
      (value) => value === 'poster' || value === 'paper' || value === 'print'
    );

    if (explicitCanvasType) {
      const [, frame] = semantic.split(':');
      semantic = frame === 'print-only' ? 'canvas:unframed' : `canvas:${frame}`;
    } else if (explicitPaperType) {
      const [, frame] = semantic.split(':');
      semantic = frame === 'unframed' ? 'paper:print-only' : `paper:${frame}`;
    }

    return semantic;
  };

  C.prototype.semanticForFrameInput = function semanticForFrameInputV46(input) {
    if (!input) return null;

    const material = input.dataset.materialScope;
    const frame = input.value;

    if (material === 'paper') {
      if (frame === 'print-only') return 'paper:print-only';
      if (['black', 'oak', 'white'].includes(frame)) return `paper:${frame}`;
    }

    if (material === 'canvas') {
      if (frame === 'unframed') return 'canvas:unframed';
      if (['black', 'oak', 'white'].includes(frame)) return `canvas:${frame}`;
    }

    return this.canonicalFinish(input.dataset.nativeFinish);
  };

  C.prototype.prepareIndexes = function prepareIndexesV46() {
    basePrepareIndexes.call(this);

    this.variantBySemanticCombination = new Map();

    this.variants.forEach((variant) => {
      const size = variant.options[this.sizeOptionKey];
      const semantic = this.canonicalVariantFinish(variant);
      if (!semantic || !size) return;

      const key = `${semantic}::${this.normalize(size)}`;
      const existing = this.variantBySemanticCombination.get(key);

      if (!existing || (!existing.available && variant.available)) {
        this.variantBySemanticCombination.set(key, variant);
      }
    });
  };

  C.prototype.configureStaticAvailability = function configureStaticAvailabilityV46() {
    this.frameInputs.forEach((input) => {
      const isPermanent = input.hasAttribute('disabled') && Boolean(input.dataset.disabledReason);
      input.dataset.permanentlyDisabled = isPermanent ? 'true' : 'false';

      if (isPermanent) {
        input.disabled = true;
        return;
      }

      const wantedSemantic = this.semanticForFrameInput(input);
      const exists = Boolean(wantedSemantic) && this.variants.some(
        (variant) => this.canonicalVariantFinish(variant) === wantedSemantic
      );

      input.disabled = !exists;
    });

    this.materialInputs.forEach((input) => {
      const scopedFrames = this.frameInputs.filter(
        (frameInput) => frameInput.dataset.materialScope === input.value
      );
      input.disabled = !scopedFrames.some((frameInput) => !frameInput.disabled);
    });
  };

  C.prototype.updateFrameAvailability = function updateFrameAvailabilityV46(material) {
    this.frameInputs.forEach((input) => {
      if (input.dataset.materialScope !== material) return;

      if (input.dataset.permanentlyDisabled === 'true') {
        input.disabled = true;
        return;
      }

      const wantedSemantic = this.semanticForFrameInput(input);
      input.disabled = !wantedSemantic || !this.variants.some(
        (variant) => this.canonicalVariantFinish(variant) === wantedSemantic
      );
    });
  };

  C.prototype.findVariant = function findVariantV46(finish, size) {
    const exact = baseFindVariant.call(this, finish, size);
    if (exact) {
      const exactSemantic = this.canonicalVariantFinish(exact);
      const requestedSemantic = this.canonicalFinish(finish);
      if (!requestedSemantic || exactSemantic === requestedSemantic) return exact;
    }

    const semantic = this.canonicalFinish(finish);
    if (!semantic || !size) return null;

    return this.variantBySemanticCombination?.get(
      `${semantic}::${this.normalize(size)}`
    ) || null;
  };

  C.prototype.findClosestExistingSize = function findClosestExistingSizeV46(
    finish,
    desiredSize,
    { preferAvailable = false } = {}
  ) {
    const semantic = this.canonicalFinish(finish);
    if (!semantic) {
      return baseFindClosestExistingSize.call(this, finish, desiredSize, { preferAvailable });
    }

    let candidates = this.variants.filter(
      (variant) => this.canonicalVariantFinish(variant) === semantic
    );

    if (preferAvailable && candidates.some((variant) => variant.available)) {
      candidates = candidates.filter((variant) => variant.available);
    }

    if (!candidates.length) return null;

    const desiredMetric = this.sizeMetrics.get(desiredSize)?.longEdge;
    if (!Number.isFinite(desiredMetric)) {
      return candidates[0].options[this.sizeOptionKey];
    }

    return candidates
      .map((variant) => {
        const candidateSize = variant.options[this.sizeOptionKey];
        const metric = this.sizeMetrics.get(candidateSize)?.longEdge;
        return {
          size: candidateSize,
          distance: Number.isFinite(metric)
            ? Math.abs(metric - desiredMetric)
            : Number.POSITIVE_INFINITY,
        };
      })
      .sort((a, b) => a.distance - b.distance)[0]?.size || null;
  };

  C.prototype.stateFromVariant = function stateFromVariantV46(variant) {
    const state = baseStateFromVariant.call(this, variant);
    const semantic = this.canonicalVariantFinish(variant);
    if (!semantic) return state;

    const [material, frame] = semantic.split(':');
    state.material = material;
    state.frame = frame;
    state.mount = material === 'paper' ? state.mount : 'no';

    return state;
  };

  C.prototype.isSemanticSelectable = function isSemanticSelectableV46(semantic) {
    if (!semantic) return false;
    const input = this.frameInputs.find(
      (candidate) => this.semanticForFrameInput(candidate) === semantic
    );
    return Boolean(input && !input.disabled);
  };

  C.prototype.coerceVariantToSelectable = function coerceVariantToSelectableV46(variant) {
    if (!variant) return null;

    const semantic = this.canonicalVariantFinish(variant);
    if (!semantic || this.isSemanticSelectable(semantic)) return variant;

    const size = variant.options[this.sizeOptionKey];
    const [material] = semantic.split(':');
    const preferredSemantics = material === 'canvas'
      ? ['canvas:unframed', 'paper:print-only', 'paper:black', 'paper:oak', 'paper:white']
      : ['paper:print-only', 'paper:black', 'paper:oak', 'paper:white', 'canvas:unframed'];

    for (const preferred of preferredSemantics) {
      if (!this.isSemanticSelectable(preferred)) continue;
      const match = this.variantBySemanticCombination?.get(
        `${preferred}::${this.normalize(size)}`
      );
      if (match?.available) return match;
    }

    return this.variants.find((candidate) => {
      const candidateSemantic = this.canonicalVariantFinish(candidate);
      return candidate.available && this.isSemanticSelectable(candidateSemantic);
    }) || variant;
  };

  C.prototype.restoreInitialState = function restoreInitialStateV46() {
    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    const liquidVariantId = this.dataset.currentVariantId;
    const selectVariantId = this.variantSelect.value;

    const requestedVariant =
      this.variantById.get(String(urlVariantId || '')) ||
      this.variantById.get(String(liquidVariantId || '')) ||
      this.variantById.get(String(selectVariantId || '')) ||
      this.variants.find((variant) => variant.available) ||
      this.variants[0];

    const initialVariant = this.coerceVariantToSelectable(requestedVariant);

    this.applyVariant(initialVariant, {
      updateHistory: true,
      replaceHistory: true,
      announce: false,
    });
  };

  C.prototype.restoreFromUrl = function restoreFromUrlV46() {
    const variantId = new URL(window.location.href).searchParams.get('variant');
    const requestedVariant = this.variantById.get(String(variantId || ''));
    if (!requestedVariant) return;

    const variant = this.coerceVariantToSelectable(requestedVariant);
    if (!variant) return;

    this.isRestoringHistory = true;
    this.applyVariant(variant, { updateHistory: false, announce: true });
    this.isRestoringHistory = false;

    if (variant.id !== requestedVariant.id) {
      this.updateUrl(variant.id, true);
    }
  };
})();
